import type { ReactNode } from 'react'
import { DialogContent, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

const widths = { md: 'sm:max-w-3xl', lg: 'sm:max-w-4xl', xl: 'sm:max-w-5xl' }

/**
 * The demo's in-place detail dialog: a header bar, a scrolling body and an optional footer, full
 * screen on a phone. Render inside a `Dialog`.
 */
export function EditorModalContent({
  title,
  description,
  size = 'md',
  footer,
  children,
  onOpenAutoFocus,
  onCloseAutoFocus,
  closeDisabled = false,
}: {
  title: ReactNode
  description?: ReactNode
  size?: keyof typeof widths
  footer?: ReactNode
  children: ReactNode
  onOpenAutoFocus?: (event: Event) => void
  onCloseAutoFocus?: (event: Event) => void
  closeDisabled?: boolean
}) {
  return (
    <DialogContent
      aria-describedby={undefined}
      className={cn(
        'flex max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-none flex-col gap-0 overflow-hidden p-0',
        widths[size],
        closeDisabled && '[&>[data-slot=dialog-close]]:pointer-events-none [&>[data-slot=dialog-close]]:opacity-30',
      )}
      onCloseAutoFocus={onCloseAutoFocus}
      onEscapeKeyDown={(event) => closeDisabled && event.preventDefault()}
      onInteractOutside={(event) => closeDisabled && event.preventDefault()}
      onOpenAutoFocus={onOpenAutoFocus}
    >
      <header className="shrink-0 border-b px-4 py-3 pr-12 sm:px-6">
        <DialogTitle className="text-base font-semibold [overflow-wrap:anywhere]">{title}</DialogTitle>
        {description ? <p className="mt-0.5 text-sm text-muted-foreground">{description}</p> : null}
      </header>
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6">{children}</div>
      {footer ? <footer className="shrink-0 border-t px-4 py-3 sm:px-6">{footer}</footer> : null}
    </DialogContent>
  )
}
